import { HttpClient } from '@adapters/HttpClient';

const BASE = '/migration';

export type MigrationRunStatus = 'pending' | 'running' | 'paused' | 'completed' | 'failed';
export type EntityProgressStatus = 'pending' | 'running' | 'completed' | 'failed';
export type MigrationScope = 'total' | 'partial';

export interface MigrationChild {
  id: number;
  name: string;
  latest_run: {
    id: number;
    status: MigrationRunStatus;
    external_team_id: string;
    started_at: string;
    finished_at: string | null;
    scope?: MigrationScope;
  } | null;
}

export interface MigrationEntityProgress {
  id: number;
  entity_type: string;
  total_count: number;
  migrated_count: number;
  failed_count: number;
  status: EntityProgressStatus;
  batch_id?: string;
}

export interface MigrationRun {
  id: number;
  team_id: number;
  child_team_id: number;
  external_team_id: string;
  scope?: MigrationScope;
  status: MigrationRunStatus;
  started_at: string;
  paused_at: string | null;
  finished_at: string | null;
  entity_progress: MigrationEntityProgress[];
  child_team?: {
    id: number;
    name: string;
  };
}

export interface MigrationChildrenResponse {
  data: MigrationChild[];
}

export interface MigrationRunResponse {
  data: MigrationRun | null;
}

export interface MigrationProgressEntity {
  id: number;
  entity_type: string;
  total_count: number;
  migrated_count: number;
  failed_count: number;
  status: EntityProgressStatus;
  progress_percentage: number;
  started_at: string | null;
  finished_at: string | null;
  last_error: string | null;
}

export interface MigrationProgressResponse {
  migration_run: Omit<MigrationRun, 'entity_progress'> & { child_team?: { id: number; name: string } };
  entities: MigrationProgressEntity[];
  overall_progress_percentage: number;
}

export interface MigrationError {
  id: number;
  migration_entity_progress_id: number;
  external_id: string;
  entity_type: string;
  error_message: string;
  raw_payload: Record<string, unknown>;
  retry_count: number;
  created_at: string;
}

export interface MigrationActivityItem {
  id: number;
  migration_run_id: number;
  user_id: number;
  action: string;
  entity_type: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  user: {
    id: number;
    name: string;
  };
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

export interface MigrationDeleteResponse {
  data: {
    deleted: Record<string, number>;
  };
}

export type MigrationReconciliationCheckKey = 'cod_payouts';
export type MigrationReconciliationSeverity = 'critical' | 'warning' | 'info';

export interface MigrationCodPayoutsSample {
  cod_payout_id: number;
  total_amount: string;
  items_sum: string;
  difference: string;
}

export interface MigrationCodPayoutsMetrics {
  legacy_settlements_count: number;
  legacy_settlements_total: string;
  warehouse_courier_cod_payments_count: number;
  warehouse_courier_cod_payments_total: string;
  legacy_payout_items_count: number;
  legacy_payout_items_total: string;
  warehouse_cod_payout_items_count: number;
  warehouse_cod_payout_items_total: string;
  warehouse_cod_payouts_count: number;
  warehouse_cod_payouts_total: string;
  payouts_with_divergent_total: number;
}

export interface MigrationReconciliationCheck {
  key: MigrationReconciliationCheckKey | string;
  passed: boolean;
  severity: MigrationReconciliationSeverity;
  message: string;
  metrics: MigrationCodPayoutsMetrics | Record<string, string | number>;
  samples?: MigrationCodPayoutsSample[];
}

export interface MigrationReconciliationData {
  run_id: number;
  child_team_id: number;
  external_team_id: string;
  status: MigrationRunStatus;
  checked_at: string;
  available_checks: MigrationReconciliationCheckKey[];
  checks: MigrationReconciliationCheck[];
}

export interface MigrationReconciliationResponse {
  data: MigrationReconciliationData;
}

export class MigrationApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getChildren(): Promise<MigrationChildrenResponse> {
    return this.httpClient.get<MigrationChildrenResponse>(`${BASE}/children`);
  }

  async getRun(childTeamId: number): Promise<MigrationRunResponse> {
    return this.httpClient.get<MigrationRunResponse>(`${BASE}/children/${childTeamId}/run`);
  }

  async start(params: {
    childTeamId: number;
    externalTeamId: string;
    stockFile?: File;
    scope?: MigrationScope;
  }): Promise<{ data: MigrationRun }> {
    const { childTeamId, externalTeamId, stockFile, scope = 'total' } = params;
    const formData = new FormData();
    formData.append('child_team_id', String(childTeamId));
    formData.append('external_team_id', externalTeamId);
    formData.append('scope', scope);
    if (stockFile) {
      formData.append('stock_file', stockFile);
    }
    return this.httpClient.post<{ data: MigrationRun }>(`${BASE}/runs`, formData);
  }

  async pause(runId: number): Promise<{ data: MigrationRun }> {
    return this.httpClient.post<{ data: MigrationRun }>(`${BASE}/runs/${runId}/pause`, {});
  }

  async resume(runId: number): Promise<{ data: MigrationRun }> {
    return this.httpClient.post<{ data: MigrationRun }>(`${BASE}/runs/${runId}/resume`, {});
  }

  async getProgress(runId: number): Promise<MigrationProgressResponse> {
    return this.httpClient.get<MigrationProgressResponse>(`${BASE}/runs/${runId}/progress`);
  }

  async retry(progressId: number): Promise<{ data: MigrationEntityProgress }> {
    return this.httpClient.post<{ data: MigrationEntityProgress }>(`${BASE}/entities/${progressId}/retry`, {});
  }

  async getErrors(
    runId: number,
    params?: { entity_type?: string; page?: number; per_page?: number },
  ): Promise<PaginatedResponse<MigrationError>> {
    const queryParams: Record<string, string | number> = {};
    if (params?.entity_type) queryParams.entity_type = params.entity_type;
    if (params?.page) queryParams.page = params.page;
    if (params?.per_page) queryParams.per_page = params.per_page;

    return this.httpClient.get<PaginatedResponse<MigrationError>>(
      `${BASE}/runs/${runId}/errors`,
      Object.keys(queryParams).length ? queryParams : undefined,
    );
  }

  async getActivity(
    runId: number,
    params?: { page?: number; per_page?: number },
  ): Promise<PaginatedResponse<MigrationActivityItem>> {
    const queryParams: Record<string, string | number> = {};
    if (params?.page) queryParams.page = params.page;
    if (params?.per_page) queryParams.per_page = params.per_page;

    return this.httpClient.get<PaginatedResponse<MigrationActivityItem>>(
      `${BASE}/runs/${runId}/activity`,
      Object.keys(queryParams).length ? queryParams : undefined,
    );
  }

  async deleteRun(runId: number): Promise<MigrationDeleteResponse> {
    return this.httpClient.delete<MigrationDeleteResponse>(`${BASE}/runs/${runId}`);
  }

  async getReconciliation(
    runId: number,
    checks?: MigrationReconciliationCheckKey[],
  ): Promise<MigrationReconciliationResponse> {
    const queryParams: Record<string, string> = {};
    if (checks?.length) {
      queryParams.checks = checks.join(',');
    }

    return this.httpClient.get<MigrationReconciliationResponse>(
      `${BASE}/runs/${runId}/reconciliation`,
      Object.keys(queryParams).length ? queryParams : undefined,
    );
  }
}
